import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Box, Fab, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Timeline from "react-calendar-timeline";
// Import official CSS (required for timeline styling)
import "react-calendar-timeline/lib/Timeline.css";
import moment from "moment";
import { FlexibleDataItem, ApiEndpoint, Entity } from "../../types";
import { getDataItemName, getDataItemDescription } from "../../utilities/dataSourceAdapters";
import { useUIActions, useUIStore } from "../../store/UI";
import { useAuthSelector } from "../../store/auth";
import { useSelectionActions } from "../../store/selection";
import { usePopupActions } from "../../store/popupStore";
import { entityIsMedia } from "../../utilities/entityModalUtils";
import { hasValidGeometry } from "../../utilities/geoUtils";

function stringField(item: FlexibleDataItem, key: string): string | undefined {
  const value = (item as unknown as { [k: string]: unknown })[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function timelineDisplayName(item: FlexibleDataItem, endpoint: ApiEndpoint | null | undefined): string {
  const mapped = getDataItemName(item, endpoint || undefined);
  const generic = !mapped || /^(Fulcrum )?Record(\s|$)/i.test(mapped);
  if (!generic) return mapped;
  return stringField(item, "_title") || stringField(item, "title") || mapped || `${endpoint?.name || "Item"} ${item.id}`;
}

interface CalendarEvent {
  id: string | number;
  title: string;
  description?: string;
  startDate: Date;
  endDate?: Date;
  item: FlexibleDataItem;
  color: string;
}

interface CalendarViewProps {
  activeEndpoint: ApiEndpoint | null;
  currentTab: number;
  height?: string;
}

const CalendarView: React.FC<CalendarViewProps> = ({ activeEndpoint, currentTab, height = "350px" }) => {
  const theme = useTheme();
  const { user: profile } = useAuthSelector();
  const { setSelectedEntityId } = useSelectionActions();
  const { openGeoPopup, openMediaViewer, openNonGeoPopup } = usePopupActions();

  const dataDrawerMode = useUIStore((state) => state.dataDrawerMode);
  const { openDataDrawerCompact } = useUIActions();

  const isDataDrawerOpen = dataDrawerMode !== "hidden";

  const [timelineData, setTimelineData] = useState<FlexibleDataItem[]>([]);

  // All hooks must be at the top level (React rules)
  const shouldShow = activeEndpoint?.name && (currentTab === 1 || currentTab === 2) && activeEndpoint?.dateConfig?.hasDateFilter;

  // && isTimelineVisibleForEndpoint(activeEndpoint.name); // ignore this condition the visibility is handled from component rendering level

  // Timeline visibility logic working correctly

  // Data fetching (always run hooks, but conditional logic inside)
  useEffect(() => {
    if (!shouldShow) return; // Conditional logic inside hook, not conditional hook
    const fetchTimelineData = async () => {
      if (activeEndpoint?.dateConfig?.hasDateFilter && profile) {
        try {
          const { fetchEndpointData } = await import("../../utilities/dataSourceAdapters");

          const result = await fetchEndpointData(
            activeEndpoint,
            profile,
            {
              fields: "id,name,description,scheduled_date,scheduled_end_date,date_recorded"
            },
            false
          );

          setTimelineData(result.data);
        } catch (error) {
          console.error("[CalendarView] Error fetching timeline data:", error);
          setTimelineData([]);
        }
      } else {
        setTimelineData([]);
      }
    };

    fetchTimelineData();
  }, [activeEndpoint, profile, shouldShow]);

  // Event handling (moved from CalendarContainer)
  const handleTimelineEventClick = useCallback(
    (event: CalendarEvent) => {
      // Open DataDrawer if it's closed
      if (!isDataDrawerOpen) {
        openDataDrawerCompact();
      }

      // Set the selected entity
      setSelectedEntityId(event.item.id);
      if (hasValidGeometry(event.item as Entity)) {
        openGeoPopup();
      } else {
        if (entityIsMedia(event.item as Entity)) {
          openMediaViewer();
        } else {
          openNonGeoPopup();
        }
      }
    },
    [isDataDrawerOpen, openDataDrawerCompact, openGeoPopup, openMediaViewer, openNonGeoPopup, setSelectedEntityId]
  );

  // Create a color ramp based on theme colors
  const generateTimelineColors = useMemo(() => {
    const primary = theme.palette.primary.main;
    const secondary = theme.palette.secondary.main;
    const success = theme.palette.success?.main || "#4caf50";
    const warning = theme.palette.warning?.main || "#ff9800";
    const info = theme.palette.info?.main || "#2196f3";
    const error = theme.palette.error?.main || "#f44336";

    // Create a color ramp using theme colors + variations
    const colorRamp = [
      primary,
      secondary,
      success,
      info,
      warning,
      // Add lighter/darker variations
      theme.palette.primary.light || primary,
      theme.palette.secondary.light || secondary,
      theme.palette.primary.dark || primary,
      theme.palette.secondary.dark || secondary,
      error
    ];

    return (index: number) => {
      return colorRamp[index % colorRamp.length];
    };
  }, [theme]);

  // Extract date configuration from activeEndpoint
  const dateConfig = activeEndpoint?.dateConfig;
  const hasDateConfig = dateConfig?.hasDateFilter ?? false;
  const isFullscreenMode = currentTab === 2;

  // Parse events from data based on date configuration
  const { timelineItems, timelineGroups } = useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(`🗓️ [CalendarView] Processing ${timelineData?.length || 0} items for ${activeEndpoint?.name}`);
    }

    if (!hasDateConfig || !dateConfig || !timelineData) {
      return { timelineItems: [], timelineGroups: [] };
    }

    const items: any[] = [];

    timelineData.forEach((item, index) => {
      let startDate: moment.Moment | null = null;
      let endDate: moment.Moment | null = null;

      // Development logging for date field debugging
      if (process.env.NODE_ENV === "development" && index < 2) {
        const dateFields = {
          scheduled_date: (item as any).scheduled_date,
          scheduled_end_date: (item as any).scheduled_end_date
        };
        if (!dateFields.scheduled_date && !dateFields.scheduled_end_date) {
          console.warn(`🗓️ [CalendarView] No date fields found for item ${item.id}`);
        }
      }

      // Handle single date field
      if (dateConfig.dateField) {
        const dateValue = (item as any)[dateConfig.dateField];
        if (dateValue) {
          try {
            startDate = moment(dateValue);
            if (!startDate.isValid()) startDate = null;
          } catch (e) {
            console.warn("Invalid date value:", dateValue);
          }
        }
      }

      // Handle start/end date fields (takes precedence over single date)
      if (dateConfig.startDateField) {
        const startDateValue = (item as any)[dateConfig.startDateField];
        if (startDateValue) {
          try {
            startDate = moment(startDateValue);
            if (!startDate.isValid()) startDate = null;
          } catch (e) {
            console.warn("Invalid start date value:", startDateValue, e);
          }
        }
      }

      if (dateConfig.endDateField) {
        const endDateValue = (item as any)[dateConfig.endDateField];
        if (endDateValue) {
          try {
            endDate = moment(endDateValue);
            if (!endDate.isValid()) endDate = null;
          } catch (e) {
            console.warn("Invalid end date value:", endDateValue, e);
          }
        }
      }

      // Only create timeline item if we have a valid start date
      if (startDate) {
        // If no end date, make it a single day event
        if (!endDate) {
          endDate = moment(startDate).add(1, "day");
        }

        const displayName = timelineDisplayName(item, activeEndpoint);

        items.push({
          id: item.id,
          group: 1, // Single group for now
          title: displayName,
          start_time: startDate.valueOf(),
          end_time: endDate.valueOf(),
          canMove: false,
          canResize: false,
          canChangeGroup: false,
          itemProps: {
            title: displayName,
            style: {
              background: generateTimelineColors(index),
              color: theme.palette.getContrastText(generateTimelineColors(index)),
              borderRadius: theme.shape.borderRadius,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[2], // Slightly more prominent shadow
              fontSize: theme.typography.body2.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              // Add hover effect
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: theme.shadows[4],
                opacity: 0.9
              }
            }
          }
        });
      }
    });

    // Create a single group for all items
    const groups = [
      {
        id: 1,
        title: activeEndpoint?.name || "Timeline"
      }
    ];

    if (process.env.NODE_ENV === "development") {
      console.log(`🗓️ [CalendarView] Created ${items.length} timeline items`);
    }

    return { timelineItems: items, timelineGroups: groups };
  }, [timelineData, dateConfig, hasDateConfig, activeEndpoint, theme, generateTimelineColors]);

  // Calculate default time range based on events (must be before early return)
  const { defaultTimeStart, defaultTimeEnd } = useMemo(() => {
    if (timelineItems.length === 0) {
      // Default to current month if no events
      return {
        defaultTimeStart: moment().startOf("month"),
        defaultTimeEnd: moment().endOf("month")
      };
    }

    // Open on the year of the most recent item. All-time (2021–2026) still
    // collapses 470 single-day bars into overlapping slivers; a year is enough
    // context without that. The user can pan/zoom to other years.
    const latest = moment.max(timelineItems.map((item) => moment(item.end_time)));

    return {
      defaultTimeStart: moment(latest).startOf("year"),
      defaultTimeEnd: moment(latest).endOf("year")
    };
  }, [timelineItems]);

  // The visible window is controlled so the header arrows can page through time.
  // Dragging and zooming still flow back in through onTimeChange.
  const [visibleWindow, setVisibleWindow] = useState<{ start: number; end: number } | null>(null);

  useEffect(() => {
    setVisibleWindow({ start: defaultTimeStart.valueOf(), end: defaultTimeEnd.valueOf() });
  }, [defaultTimeStart, defaultTimeEnd]);

  // One click pages by the width of the current window, so the step follows zoom.
  const shiftVisibleWindow = useCallback(
    (direction: -1 | 1) => {
      setVisibleWindow((current) => {
        const start = current?.start ?? defaultTimeStart.valueOf();
        const end = current?.end ?? defaultTimeEnd.valueOf();
        const step = (end - start) * direction;
        return { start: start + step, end: end + step };
      });
    },
    [defaultTimeStart, defaultTimeEnd]
  );

  // Early return after all hooks (React rules compliance)
  if (!shouldShow) {
    return null; // No empty divs!
  }

  if (!hasDateConfig) {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 3,
          m: 1,
          height: height === "auto" ? "auto" : height,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius * 2,
          textAlign: "center"
        }}
      >
        <Typography variant='body1' color='text.secondary' sx={{ fontWeight: theme.typography.fontWeightMedium }}>
          📅 Calendar view is not available for this endpoint.
          <br />
          Date configuration is required.
        </Typography>
      </Paper>
    );
  }

  if (timelineItems.length === 0) {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 3,
          m: 1,
          height: height === "auto" ? "auto" : height,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius * 2,
          textAlign: "center"
        }}
      >
        <Typography variant='h6' gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: theme.typography.fontWeightBold }}>
          📅 {activeEndpoint?.name || "Calendar"} Timeline
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          No scheduled items found with valid dates.
        </Typography>
      </Paper>
    );
  }

  // Height is controlled by UIStore - no local overrides

  // Arrows sit on the primary (month/year) header row, which the library renders
  // 30px tall at the top of the timeline, so the Fab is sized down to fit.
  // Colours match the map control Fabs in MapControls.
  const headerArrowSx = {
    position: "absolute" as const,
    top: 1,
    zIndex: 100,
    width: "1.75rem",
    height: "1.75rem",
    minHeight: "unset",
    backgroundColor: "#1D2939",
    borderRadius: "0.5rem",
    border: "1px solid rgba(252, 252, 253, 0.15)"
  };

  return (
    <Box
      sx={{
        position: "relative",
        top: 0,
        left: 0,
        width: "100%",
        height: height,
        zIndex: isFullscreenMode ? 1500 : 1400,
        backgroundColor: "background.paper",
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        pointerEvents: "auto",
        boxShadow: isFullscreenMode ? 4 : 1
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 1, // Reduced padding from 2 to 1
          m: 0, // Removed margin to eliminate gaps
          height: "100%",
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius
        }}
      >
        {/* Timeline header removed for more compact design */}
        <Box
          sx={{
            position: "relative", // anchors the paging arrows over the timeline header
            height: `calc(${height} - 20px)`, // Use allocated height minus padding
            width: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            "& .react-calendar-timeline": {
              width: "100% !important",
              maxWidth: "100%",
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary
            },
            "& .rct-outer": {
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "100%"
            },
            "& .rct-scroll": {
              overflowY: "auto !important",
              overflowX: "auto !important",
              maxHeight: "100%"
            },
            // Timeline container styling - Enhanced with theme integration
            "& .rct-calendar-wrapper": {
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[1]
            },
            // Header styling - More prominent with theme colors
            "& .rct-header": {
              backgroundColor: theme.palette.background.paper,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              color: theme.palette.text.primary
            },
            "& .rct-dateHeader": {
              color: theme.palette.text.primary,
              borderRight: `1px solid ${theme.palette.divider}`,
              fontWeight: theme.typography.fontWeightMedium
            },
            "& .rct-dateHeader-primary": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: theme.typography.fontWeightBold
            },
            "& .rct-dateHeader-secondary": {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.secondary
            },
            // Sidebar styling - Enhanced with theme
            "& .rct-sidebar": {
              backgroundColor: theme.palette.background.paper,
              borderRight: `2px solid ${theme.palette.primary.main}`,
              color: theme.palette.text.primary
            },
            "& .rct-sidebar-row": {
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              fontWeight: theme.typography.fontWeightMedium
            },
            // Grid lines - Subtle but visible
            "& .rct-vertical-line": {
              borderLeft: `1px solid ${theme.palette.divider}`,
              opacity: 0.6
            },
            "& .rct-horizontal-line": {
              borderTop: `1px solid ${theme.palette.divider}`,
              opacity: 0.6
            },
            // Today line - Prominent theme color
            "& .rct-cursor-line": {
              backgroundColor: theme.palette.error.main,
              width: "2px",
              zIndex: 10
            },
            // Timeline content area
            "& .rct-canvas": {
              backgroundColor: theme.palette.background.default
            },
            // Single-day items collapse to 1px at year zoom; keep a clickable sliver.
            "& .rct-item": {
              minWidth: "8px"
            }
          }}
        >
          <Tooltip title='Previous period' placement='right'>
            <Fab color='primary' size='small' aria-label='Previous period' onClick={() => shiftVisibleWindow(-1)} sx={{ ...headerArrowSx, left: 4 }}>
              <ChevronLeftIcon sx={{ height: "1.25rem", width: "1.25rem" }} />
            </Fab>
          </Tooltip>
          <Tooltip title='Next period' placement='left'>
            <Fab color='primary' size='small' aria-label='Next period' onClick={() => shiftVisibleWindow(1)} sx={{ ...headerArrowSx, right: 4 }}>
              <ChevronRightIcon sx={{ height: "1.25rem", width: "1.25rem" }} />
            </Fab>
          </Tooltip>
          <Timeline
            groups={timelineGroups}
            items={timelineItems}
            visibleTimeStart={visibleWindow?.start ?? defaultTimeStart.valueOf()}
            visibleTimeEnd={visibleWindow?.end ?? defaultTimeEnd.valueOf()}
            onTimeChange={(start, end, updateScrollCanvas) => {
              setVisibleWindow({ start, end });
              updateScrollCanvas(start, end);
            }}
            sidebarWidth={0} // Hide sidebar when only one group - saves space
            lineHeight={40} // Consistent row height
            itemHeightRatio={0.85} // Increased for fuller timeline items
            canMove={false}
            canResize={false}
            canChangeGroup={false}
            stackItems={true}
            itemRenderer={({ item, itemContext, getItemProps }) => {
              const label = String(itemContext.title || item.title || "");
              const itemProps = (item.itemProps || {}) as { style?: React.CSSProperties };
              // `key` must sit on the outermost element (the Tooltip).
              const { key, ...divProps } = getItemProps({
                ...itemProps,
                style: {
                  ...itemProps.style,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: 2,
                  paddingRight: 2
                }
              });

              // title={undefined} suppresses the library's native tooltip so it does
              // not duplicate the MUI one on hover.
              return (
                <Tooltip key={key} title={label} placement='top' enterDelay={150} disableInteractive>
                  <div {...divProps} title={undefined}>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                        width: "100%",
                        fontSize: 11,
                        lineHeight: 1.2,
                        pointerEvents: "none"
                      }}
                    >
                      {label}
                    </span>
                  </div>
                </Tooltip>
              );
            }}
            onItemSelect={(itemId, e, time) => {
              // Timeline item selected

              // Find the original data item
              const originalItem = timelineData.find((dataItem) => dataItem.id === itemId);
              if (!originalItem) {
                console.warn("[CalendarView] Timeline item data not found:", itemId);
                return;
              }

              // Call selection handler

              // Call the internal event handler
              handleTimelineEventClick({
                id: itemId,
                title: getDataItemName(originalItem, activeEndpoint || undefined),
                description: getDataItemDescription(originalItem, activeEndpoint || undefined),
                startDate: new Date(),
                endDate: new Date(),
                item: originalItem,
                color: generateTimelineColors(timelineData.findIndex((item) => item.id === itemId))
              });
            }}
            onItemClick={(itemId, e, time) => {
              // Double-click handler (optional secondary action)
              if (process.env.NODE_ENV === "development") {
                console.log("🗓️ [CalendarView] Double-click detected:", itemId);
              }
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default CalendarView;
